/**
 * Search Results Page
 *
 * Displays search results in AG Grid with saved search support.
 * URL: /search?q=<query>
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Input, Space, Button, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { templatesApi, instancesApi } from '@api/index';
import SearchResultsGrid, { type SearchResultRow } from '@components/grid/SearchResultsGrid';
import { SavedViewsDropdown } from '@components/views';

const { Title, Text } = Typography;

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(queryFromUrl);

  // Fetch data for search
  const { data: templates = [], isLoading: templatesLoading, refetch: refetchTemplates } = useQuery({
    queryKey: ['templates', 'search-page'],
    queryFn: () => templatesApi.list(),
    staleTime: 2 * 60 * 1000,
  });

  const { data: instances = [], isLoading: instancesLoading, refetch: refetchInstances } = useQuery({
    queryKey: ['instances', 'search-page'],
    queryFn: () => instancesApi.list({ includeCompleted: true }),
    staleTime: 2 * 60 * 1000,
  });

  const loading = templatesLoading || instancesLoading;

  // Handle search submission
  const handleSearch = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value.trim()) {
        params.set('q', value.trim());
      } else {
        params.delete('q');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  // Current filters for saved views
  const currentFilters = useMemo(() => {
    if (!queryFromUrl) return {};
    return { query: queryFromUrl };
  }, [queryFromUrl]);

  // Handle applying saved view
  const handleApplyView = useCallback(
    (filters: Record<string, unknown>) => {
      const query = (filters.query as string) || '';
      setInputValue(query);
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      setSearchParams(params);
    },
    [setSearchParams]
  );

  // Handle row click
  const handleRowClick = useCallback(
    (row: SearchResultRow) => {
      if (row.type === 'template') {
        navigate(`/templates?id=${row.id}`);
      } else {
        navigate(`/tasks?id=${row.id}`);
      }
    },
    [navigate]
  );

  // Refresh
  const handleRefresh = useCallback(() => {
    refetchTemplates();
    refetchInstances();
  }, [refetchTemplates, refetchInstances]);

  // Count results
  const resultCount = useMemo(() => {
    if (!queryFromUrl.trim()) return 0;
    const q = queryFromUrl.toLowerCase();
    let count = 0;
    templates.forEach((t) => {
      if (t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)) count++;
    });
    instances.forEach((i) => {
      const title = i.title || i.template?.title || '';
      if (title.toLowerCase().includes(q) || i.completionNotes?.toLowerCase().includes(q)) count++;
    });
    return count;
  }, [queryFromUrl, templates, instances]);

  return (
    <div style={{ padding: 24, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Search Results</Title>
          {queryFromUrl && (
            <Text type="secondary">
              {resultCount} result{resultCount !== 1 ? 's' : ''} for &ldquo;{queryFromUrl}&rdquo;
            </Text>
          )}
        </div>
        <Space>
          <SavedViewsDropdown
            viewType="search"
            currentFilters={currentFilters}
            onApplyView={handleApplyView}
          />
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Search input */}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search work packages and work items..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onSearch={handleSearch}
          enterButton="Search"
          size="large"
          allowClear
          style={{ maxWidth: 600 }}
        />
      </div>

      {/* Results grid */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <SearchResultsGrid
          templates={templates}
          instances={instances}
          query={queryFromUrl}
          loading={loading}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
}
